import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Check, Copy, ExternalLink, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { ambApi } from "@/lib/ambApi";

const BADGE = { "à faire": "bg-[#F1E4CF] text-[#5E3F10]", "en cours": "bg-[#E7D3C0] text-[#141013]", "terminée": "bg-[#DDEBDD] text-[#24502F]" };
const fmt = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

function Axis({ a }) {
  const copy = async () => { await navigator.clipboard?.writeText(a.url); toast.success("Lien copié"); };
  return (
    <li data-testid={`amb-mission-axis-${a.key}`} className="rounded-2xl border-l-4 border-[#B8893A] bg-white p-5">
      <h3 className="font-semibold text-[#4A1942]">{a.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-[#5E544D]">{a.text}</p>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
        <span className="text-[#141013]">Lien à partager :</span>
        <a href={a.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#7A5418] underline">{a.url.replace("https://", "")}<ExternalLink className="h-3 w-3" /></a>
        <button type="button" onClick={copy} data-testid={`amb-mission-copy-${a.key}`} className="inline-flex items-center gap-1 rounded-full border border-[#D9C8B3] px-3 py-1 text-xs hover:border-[#7A5418]"><Copy className="h-3 w-3" /> Copier</button>
      </div>
    </li>
  );
}

function Checklist({ content, mission, onSaved }) {
  const [steps, setSteps] = useState(mission.steps);
  const [comments, setComments] = useState(mission.comments || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await ambApi.put("/ambassador/mission", { steps, comments }); toast.success("Mission enregistrée"); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-8">
      <h3 className="font-display text-xl text-[#141013]">Ma checklist</h3>
      <ul className="mt-4 space-y-2">
        {content.steps.map((s) => (
          <li key={s.key}>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#E3D5C3] bg-white px-4 py-3 text-sm text-[#141013]">
              <input type="checkbox" checked={!!steps[s.key]} onChange={(e) => setSteps({ ...steps, [s.key]: e.target.checked })} data-testid={`amb-mission-step-${s.key}`} className="mt-0.5 h-4 w-4 accent-[#7A5418]" />
              {s.label}
            </label>
          </li>
        ))}
      </ul>
      <textarea value={comments} onChange={(e) => setComments(e.target.value)} maxLength={2000} rows={3} placeholder="Mes commentaires (personnes contactées, sujets qui les intéressent…)"
        data-testid="amb-mission-comments" className="mt-4 w-full rounded-xl border border-[#D9C8B3] bg-white p-4 text-sm text-[#141013] outline-none focus:border-[#7A5418]" />
      <button onClick={save} disabled={busy} data-testid="amb-mission-save" className="mt-4 flex h-12 items-center gap-2 rounded-full bg-[#141013] px-8 text-xs font-semibold uppercase tracking-[0.2em] text-[#FBF8F3] disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Enregistrer ma mission
      </button>
    </div>
  );
}

export function AmbMission() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["amb-mission"], queryFn: () => ambApi.get("/ambassador/mission").then((r) => r.data), staleTime: Infinity });
  const [notifAt, setNotifAt] = useState(null);
  useEffect(() => { if (data?.new_notification_at) setNotifAt(data.new_notification_at); }, [data?.new_notification_at]);
  const c = data?.content;
  const m = data?.mission;
  if (!c || !m) return null;
  return (
    <section id="mission" data-testid="amb-dash-mission" className="scroll-mt-28 rounded-3xl border border-[#E3D5C3] bg-[#FBF8F3] p-6 md:p-8">
      {notifAt ? <p data-testid="amb-mission-notification" className="mb-5 flex items-center gap-2 rounded-2xl bg-[#4A1942] px-4 py-3 text-sm text-[#F1DBA6]"><BellRing className="h-4 w-4 shrink-0" /> Nouvelle mission reçue le {fmt(notifAt)}</p> : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl text-[#141013] md:text-3xl">{c.title}</h2>
        <span data-testid="amb-mission-status" className={`rounded-full px-4 py-1.5 text-sm ${BADGE[m.status]}`}>{m.status} · {m.done}/{m.total}</span>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-[#5E544D]">{c.intro}</p>
      <ul className="mt-5 space-y-3">{c.axes.map((a) => <Axis key={a.key} a={a} />)}</ul>
      <h3 className="mt-6 font-semibold text-[#141013]">Ton objectif</h3>
      <p className="mt-1 text-sm leading-relaxed text-[#5E544D]">{c.goal}</p>
      <p className="mt-3 text-sm leading-relaxed text-[#5E544D]">{c.no_pressure}</p>
      {c.relay_note ? <p data-testid="amb-mission-relay-note" className="mt-4 flex gap-2 rounded-2xl bg-[#F1E4CF] p-4 text-sm leading-relaxed text-[#141013]"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#7A5418]" /><span><b>Livraison de tes soins offerts :</b> {c.relay_note}</span></p> : null}
      <p className="mt-3 text-sm font-semibold text-[#7A5418]">{c.outro}</p>
      <Checklist key={m.updated_at || "new"} content={c} mission={m} onSaved={() => qc.invalidateQueries({ queryKey: ["amb-mission"] })} />
    </section>
  );
}
